/**
 * Rebalance risk profile — result copy from the `riskProfile` query param.
 *
 * Belongs here: result layout, missing-param error, Retake, Done.
 * Does not belong here: the questionnaire or the submit call.
 * `from=APP` shows back and Retake. Otherwise View plan posts the submit `viewPlanCta`.
 * Done / back leave via sticky entry `source` (same as intro Exit).
 */
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { getBottomPadding } from '../../../../utils/sessionStorage'
import { EventNames } from '../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../utils/appBridge'
import { expertReviewSafeAreaTopStyle } from '../../expert-review/shared/expertReviewSafeArea'
import { RebalanceTopBar } from '../ui/RebalanceTopBar'
import {
  RebalanceQuestionnaireEventIds,
  RebalanceQuestionnaireScreen
} from '../analytics/rebalanceQuestionnaireEvents'
import { useRebalanceQuestionnaireContext } from '../RebalanceQuestionnaireProvider'
import { isOpenedFromApp, rebalanceQuestionnairePath } from '../rebalanceQuestionnairePaths'
import { getRebalanceRiskProfileCopy, parseRebalanceRiskProfile } from '../rebalanceRiskProfile'

const RISK_PROFILE_BG_PATTERN_SRC = '/images/allocation/risk-profiles-v2/bg-pattern.svg'
const RISK_PROFILE_ERROR_ASSET_SRC = '/images/mf-l2/alert-triangle.svg'

export default function RebalanceRiskProfilePage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const search = `?${searchParams.toString()}`
  const openedFromApp = isOpenedFromApp(search)
  const profile = parseRebalanceRiskProfile(searchParams.get('riskProfile'))
  const { viewPlanCta, resetSession, exitRebalanceQuestionnaire } =
    useRebalanceQuestionnaireContext()

  function handleBack() {
    exitRebalanceQuestionnaire()
  }

  if (profile == null) {
    return <RebalanceRiskProfileError onBack={handleBack} />
  }

  const copy = getRebalanceRiskProfileCopy(profile)

  return (
    <RebalanceRiskProfileReady
      name={copy.name}
      description={copy.description}
      assetSrc={copy.assetSrc}
      openedFromApp={openedFromApp}
      onBack={handleBack}
      onRetake={() => {
        resetSession()
        navigate(rebalanceQuestionnairePath(search), { replace: true })
      }}
      showViewPlan={viewPlanCta != null}
      onDone={handleBack}
      onViewPlan={() => {
        if (viewPlanCta == null) return
        postCTA(JSON.stringify(viewPlanCta))
      }}
    />
  )
}

function RebalanceRiskProfileReady({
  name,
  description,
  assetSrc,
  openedFromApp,
  onBack,
  onRetake,
  showViewPlan,
  onDone,
  onViewPlan
}: {
  name: string
  description: string
  assetSrc: string
  openedFromApp: boolean
  onBack: () => void
  onRetake: () => void
  showViewPlan: boolean
  onDone: () => void
  onViewPlan: () => void
}) {
  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: RebalanceQuestionnaireScreen.RiskProfile }
    })
  }, [])

  return (
    <div
      className="animate-fade-250 relative flex h-screen min-h-0 flex-col overflow-hidden"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`,
        background: 'linear-gradient(180deg, #F4DAC6 0%, #FFF 100%)'
      }}
    >
      <img
        src={RISK_PROFILE_BG_PATTERN_SRC}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 z-0 h-auto w-full max-w-none -translate-x-1/2 object-cover opacity-40 [@media(min-height:680px)]:w-[140%] [@media(min-height:780px)]:w-[180%]"
      />
      {openedFromApp ? (
        <div className="relative z-10 shrink-0 px-5">
          <RebalanceTopBar
            onBack={onBack}
            analyticsScreen={RebalanceQuestionnaireScreen.RiskProfile}
            testLocatorBack="rebalance-risk-profile-back"
          />
        </div>
      ) : null}
      <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center overflow-auto no-scrollbar px-5">
        <div className="my-auto flex w-full max-w-[303px] flex-col items-center gap-9">
          <div className="flex h-[164px] w-[164px] items-center justify-center mix-blend-soft-light" aria-hidden>
            <img src={assetSrc} alt="" className="h-[164px] w-[164px] object-contain" />
          </div>
          <div className="flex w-full flex-col items-center gap-4 text-center">
            <div className="flex flex-col items-center">
              <p className="text-[14px] font-[400] leading-[140%] text-[#232323] opacity-60">
                Recommended allocation strategy
              </p>
              <h1
                className="mt-[-4px] bg-clip-text text-[32px] font-[500] leading-[140%] text-transparent opacity-80"
                style={{
                  backgroundImage:
                    'linear-gradient(93deg, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(0, 0, 0, 0.9) 100%)'
                }}
                test-locator="rebalance-risk-profile-title"
              >
                {name}
              </h1>
            </div>
            <div className="text-[14px] leading-[150%] text-[rgba(0,0,0,0.7)]">
              <RenderHTML innerHTMLText={description} />
            </div>
          </div>
        </div>
      </div>
      <div className="relative z-10 flex w-full shrink-0 items-end px-5 pt-1">
        {openedFromApp ? (
          <PrimaryButton
            testLocator="rebalance-risk-profile-retake"
            onClick={() => {
              lightImpactVibrate()
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  ScreenName: RebalanceQuestionnaireScreen.RiskProfile,
                  eventId: RebalanceQuestionnaireEventIds.Retake,
                  buttonLabel: 'Retake test'
                }
              })
              onRetake()
            }}
            label="Retake test"
            primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px] !bg-[#232323]"
          />
        ) : (
          <ProfileExitButton showViewPlan={showViewPlan} onDone={onDone} onViewPlan={onViewPlan} />
        )}
      </div>
    </div>
  )
}

function RebalanceRiskProfileError({ onBack }: { onBack: () => void }) {
  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: { ScreenName: RebalanceQuestionnaireScreen.RiskProfile }
    })
  }, [])

  return (
    <div
      className="animate-fade-250 relative flex h-screen min-h-0 flex-col overflow-hidden"
      style={{
        ...expertReviewSafeAreaTopStyle(),
        paddingBottom: `calc(${getBottomPadding()}px + 16px)`,
        background: 'linear-gradient(180deg, #FFB4A8 0%, #FFF 100%)'
      }}
      role="alert"
    >
      <img
        src={RISK_PROFILE_BG_PATTERN_SRC}
        alt=""
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 z-0 h-auto w-full max-w-none -translate-x-1/2 object-cover opacity-40 [@media(min-height:680px)]:w-[140%] [@media(min-height:780px)]:w-[180%]"
      />
      <div className="relative z-10 shrink-0 px-5">
        <RebalanceTopBar
          onBack={onBack}
          analyticsScreen={RebalanceQuestionnaireScreen.RiskProfile}
          testLocatorBack="rebalance-risk-profile-error-back"
        />
      </div>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col items-center overflow-auto no-scrollbar px-5">
        <div className="my-auto flex w-full max-w-[303px] flex-col items-center gap-10">
          <div className="flex h-[124px] w-[124px] items-center justify-center" aria-hidden>
            <img src={RISK_PROFILE_ERROR_ASSET_SRC} alt="" className="h-[88px] w-[88px] object-contain" />
          </div>
          <div className="flex w-full flex-col items-center gap-3 text-center">
            <h1
              className="text-[28px] font-[500] leading-[130%] text-[#232323]"
              test-locator="rebalance-risk-profile-error-title"
            >
              Something went wrong
            </h1>
            <p className="text-[14px] font-[400] leading-[150%] text-[rgba(0,0,0,0.7)]">
              We couldn’t load your allocation strategy. Go back and try again.
            </p>
          </div>
        </div>
      </div>
      <div className="relative z-10 flex w-full shrink-0 items-end px-5 pt-1">
        <PrimaryButton
          testLocator="rebalance-risk-profile-error-back"
          onClick={() => {
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: {
                ScreenName: RebalanceQuestionnaireScreen.RiskProfile,
                eventId: RebalanceQuestionnaireEventIds.GoBack,
                buttonLabel: 'Go back'
              }
            })
            onBack()
          }}
          label="Go back"
          primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px] !bg-[#232323]"
        />
      </div>
    </div>
  )
}

function ProfileExitButton({
  showViewPlan,
  onDone,
  onViewPlan
}: {
  showViewPlan: boolean
  onDone: () => void
  onViewPlan: () => void
}) {
  const label = showViewPlan ? 'View plan' : 'Done'
  const eventId = showViewPlan ? RebalanceQuestionnaireEventIds.ViewPlan : RebalanceQuestionnaireEventIds.Done

  return (
    <PrimaryButton
      testLocator={showViewPlan ? 'rebalance-risk-profile-view-plan' : 'rebalance-risk-profile-done'}
      onClick={() => {
        lightImpactVibrate()
        trackEvent({
          eventName: EventNames.ButtonTap,
          payload: {
            ScreenName: RebalanceQuestionnaireScreen.RiskProfile,
            eventId,
            buttonLabel: label
          }
        })
        if (showViewPlan) {
          onViewPlan()
          return
        }
        onDone()
      }}
      label={label}
      primaryButtonClassName="w-full !h-[56px] !font-[500] !rounded-[16px] !bg-[#232323]"
    />
  )
}
